import React, { useState, useCallback, useRef, useEffect } from "react";
import { FieldProps } from "@rjsf/utils";
import MediaField from "./MediaField";
import uploadFile from "../api/uploadFile";
import Video from "../components/Video";
import { Card, TextField, IconButton } from "@material-ui/core";
import { NavigateBefore, NavigateNext } from "@material-ui/icons";

interface VideoFieldProps extends FieldProps {
  title?: string;
}

const VideoField: React.FC<VideoFieldProps> = props => {
  const { onChange, formData } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [localThumbValue, setLocalThumbValue] = useState<string>("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const id = props.idSchema.$id;

  const videoFileUrl = formData?.video_file_url || "";
  const videoThumbnailFileUrl = formData?.video_thumbnail_file_url || "";

  useEffect(() => {
    setLocalThumbValue(videoThumbnailFileUrl || "");
  }, [videoThumbnailFileUrl]);

  const debouncedOnThumbChange = useCallback(
    (newValue: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onChange({
          ...formData,
          video_thumbnail_file_url: newValue,
        });
      }, 1000);
    },
    [onChange, formData]
  );

  const handleUploadClick = () => {
    document.getElementById("video-file-uploader_" + id)?.click();
  };

  const handleVideoUrlChange = (url: string) => {
    if (videoThumbnailFileUrl === "" && url.includes("videos/video.m3u8")) {
      onChange({
        ...formData,
        video_file_url: url,
        video_thumbnail_file_url: url.replace("videos/video.m3u8", "thumbs/thumb_screen.0000000.jpg"),
      });
    } else {
      onChange({
        ...formData,
        video_file_url: url,
      });
    }
  };

  const handleThumbInputChange = (newValue: string) => {
    setLocalThumbValue(newValue);
    debouncedOnThumbChange(newValue);
  };

  const incrementFrame = () => {
    if (!videoThumbnailFileUrl) return;
    const match = videoThumbnailFileUrl.match(/(.*)\.(\d{7})\.jpg$/);
    if (match) {
      const [, prefix, frameNumber] = match;
      const nextFrame = (parseInt(frameNumber) + 1).toString().padStart(7, "0");
      const newValue = `${prefix}.${nextFrame}.jpg`;
      onChange({
        ...formData,
        video_thumbnail_file_url: newValue,
      });
    }
  };

  const decrementFrame = () => {
    if (!videoThumbnailFileUrl) return;
    const match = videoThumbnailFileUrl.match(/(.*)\.(\d{7})\.jpg$/);
    if (match) {
      const [, prefix, frameNumber] = match;
      const prevFrame = Math.max(0, parseInt(frameNumber) - 1)
        .toString()
        .padStart(7, "0");
      const newValue = `${prefix}.${prevFrame}.jpg`;
      onChange({
        ...formData,
        video_thumbnail_file_url: newValue,
      });
    }
  };

  const videoPreview = videoFileUrl ? (
    <div style={{ width: 320, height: 200 }}>
      <Video
        videoFileUrl={videoFileUrl}
        poster={videoFileUrl.replace("videos/video.m3u8", "thumbs/thumb_screen.0000000.jpg")}
        captionFileUrl=""
      />
    </div>
  ) : null;

  return (
    <>
      <input
        id={"video-file-uploader_" + id}
        type="file"
        accept=".mp4"
        onChange={async e => {
          const file = e.target.files?.[0];
          if (file) {
            setLoading(true);
            try {
              const url = await uploadFile(file);
              const url2 = url.replace(".mp4", "/videos/video.m3u8");
              handleVideoUrlChange(url2);
            } catch (error) {
              console.error("Error uploading file:", error);
            } finally {
              setLoading(false);
            }
          }
        }}
        style={{ display: "none" }}
      />

      <MediaField
        title="Video File URL"
        value={videoFileUrl}
        onValueChange={handleVideoUrlChange}
        onUploadClicked={handleUploadClick}
        loading={loading}
        previewContent={videoPreview}
      ></MediaField>

      <div style={{ marginTop: "16px" }}>
        {videoThumbnailFileUrl && (
          <Card elevation={2} style={{ padding: "16px", textAlign: "center", marginBottom: "16px" }}>
            <img
              src={videoThumbnailFileUrl}
              alt="Video Thumbnail"
              style={{ maxHeight: 200, maxWidth: "100%", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
            />
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "8px" }}>
              <IconButton onClick={decrementFrame} disabled={!videoThumbnailFileUrl} size="small">
                <NavigateBefore />
              </IconButton>
              {videoThumbnailFileUrl.split("/").pop()}
              <IconButton onClick={incrementFrame} disabled={!videoThumbnailFileUrl} size="small">
                <NavigateNext />
              </IconButton>
            </div>
          </Card>
        )}
        <TextField
          label="Video Thumbnail File URL"
          value={localThumbValue}
          fullWidth
          disabled={loading}
          onChange={e => handleThumbInputChange(e.target.value)}
        />
      </div>
    </>
  );
};

export default VideoField;
